import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, TextField, Button, Link, Typography, Box, Alert } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import ApiService from '../services/ApiService'
import { validateEmail } from '../utils/validateForm'
import { useRecaptchaV2 } from 'hooks/useRecaptchaV2'

const ForgotPassword = () => {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const { captchaRef, token, resetCaptcha } = useRecaptchaV2(process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()

    let valid = true

    if (captchaRef.current && !token) {
      setError('Please complete the reCAPTCHA.')
      valid = false
    } else {
      setError('')
    }

    if (!validateEmail(normalizedEmail)) {
      setEmailError('Invalid email.')
      valid = false
    } else {
      setEmailError('')
    }

    if (!valid) return

    setLoading(true)
    setError('')
    setEmailError('')

    try {
      await ApiService.post('/auth/forgot-password', {
        email: normalizedEmail,
        captcha_token: token,
      })
      resetCaptcha()
      setSuccess(true)
      setEmail('')
    } catch (error) {
      resetCaptcha()
      setError(error.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    if (emailError) {
      setEmailError('')
    }
    if (error) {
      setError('')
    }
  }

  return (
    <Container
      maxWidth="xs"
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <Helmet>
        <title>Treegress - Forgot Password</title>
      </Helmet>
      <Box sx={{ border: '1px solid #C4C4C4', borderRadius: '8px', padding: '32px' }}>
        <Typography variant="h6" align="center" fontWeight="400" gutterBottom>
          Forgot password
        </Typography>
        
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              If an account exists for that email, we&apos;ve sent a reset link.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ width: '250px', textTransform: 'none' }}
              >
                Back to Sign in
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Please enter the email address you used when creating your account.
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                label="Work Email"
                fullWidth
                margin="normal"
                variant="outlined"
                value={email}
                onChange={handleEmailChange}
                required
                error={!!emailError}
                helperText={emailError+' '}
                disabled={loading}
                inputProps={{
                  form: {
                    autocomplete: 'off',
                  }
                }}
              />
              <Box ref={captchaRef} sx={{ margin: "0 0 25px 0" }} />
              {error && <Typography color="error" sx={{ margin: '-26px 0 8px 14px', fontSize: '0.75rem' }}>{error}</Typography>}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                  sx={{ width: '250px', textTransform: 'none' }}
                >
                  {loading ? 'Sending...' : 'Send reset email'}
                </Button>
              </Box>
            </form>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Link href="/login" variant="body2">
                Back to Sign in
              </Link>
            </Box>
          </>
        )}
      </Box>
    </Container>
  )
}

export default ForgotPassword
